'use client';

import * as React from 'react';
import { Controller, type FieldPath, type FieldValues, useController, useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { Label } from '@/slw/shared/ui/label';
import { Checkbox as BaseCheckbox } from '@/slw/shared/ui/checkbox';
import { cn } from '@/slw/shared/lib/utils';

// ----------------------------------------------------------------------

interface CheckboxProps<TFieldValues extends FieldValues>
  extends Omit<React.ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<React.ComponentPropsWithoutRef<typeof BaseCheckbox>, 'name' | 'defaultValue' | 'onChange'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  checkboxContainerClass?: string;
  label?: string;
  labelProps?: React.ComponentPropsWithoutRef<typeof Label>;
  withError?: boolean;
  showMessError?: boolean;
  direction?: 'row' | 'column';
  onChange?: (value: string[] | boolean) => void;
  multiple?: boolean;
  className?: string;
  checkboxPos?: 'left' | 'right';
}

export function Checkbox<TFieldValues extends FieldValues>({
  name,
  helperText,
  value,
  showMessError,
  containerClass,
  label,
  labelProps,
  withError = true,
  defaultValue,
  onChange,
  direction = 'row',
  multiple = false,
  className,
  checkboxPos = 'right',
  ...other
}: CheckboxProps<TFieldValues>) {
  const { control } = useFormContext();
  const controller = useController({ name });
  const field = controller.field;
  const values = multiple ? ((field.value ?? []) as any[]) : !!field.value;
  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';
  const handleCheckboxChange = (checked: boolean) => {
    if (multiple) {
      const newValues = (field.value || []) as any[];

      if (checked) {
        newValues.push(value);
        field.onChange(newValues);
        onChange?.(newValues);

        return;
      }

      const filterValues = newValues.filter((val: any) => value !== val);

      field.onChange(filterValues);
      onChange?.(filterValues);
    } else {
      field.onChange(checked);
      onChange?.(checked);
    }
  };

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { ...fields }, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && checkboxPos === 'left' && (
            <FormLabel htmlFor={other.id ?? name} className={(cn('whitespace-nowrap'), labelProps?.className)}>
              {label}
            </FormLabel>
          )}
          <FormControl>
            <BaseCheckbox
              {...fields}
              {...other}
              id={other.id ?? name}
              value={value}
              onCheckedChange={handleCheckboxChange}
              checked={multiple ? Array.isArray(values) && values.includes(value) : !!values}
              className={cn(className, error && withError && 'border-destructive focus-visible:ring-destructive')}
            />
          </FormControl>
          {!!label && checkboxPos === 'right' && (
            <FormLabel htmlFor={other.id ?? name} className={(cn('whitespace-nowrap'), labelProps?.className)}>
              {label}
            </FormLabel>
          )}
          {helperText && withError && showMessError && (
            <p className='text-sm text-muted-foreground ml-1.5'>{helperText}</p>
          )}
          {error && withError && showMessError && <FormMessage>{error.message}</FormMessage>}
        </FormItem>
      )}
    />
  );
}
